import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

import { mainNavigation } from '@/constants/navigation'

export const isNavItemActive = (item: NavItem, path: string): boolean => {
    const current = path.split(/[?#]/)[0]

    const matches = (href: string): boolean => {
        const target = href.split('#')[0]
        if (target === '/') return current === '/'
        return current === target || current.startsWith(`${target}/`)
    }

    if (item.href) return matches(item.href)
    return (item.links ?? []).some((link) => matches(link.href))
}

const topLevelClass = 'inline-flex items-center gap-1 rounded-full px-2.5 py-2 text-[0.95rem] font-semibold text-navy transition-colors hover:bg-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy'

export const DesktopNav: FC = (): ReactNode => {
    const router = useRouter()
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const navRef = useRef<HTMLElement>(null)
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])
    const closeTimer = useRef<number | null>(null)
    const openedByHover = useRef(false)

    const clearCloseTimer = (): void => {
        if (closeTimer.current !== null) {
            window.clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }

    const scheduleClose = (): void => {
        clearCloseTimer()
        closeTimer.current = window.setTimeout(() => {
            openedByHover.current = false
            setOpenIndex(null)
        }, 180)
    }

    useEffect(() => {
        const close = (): void => setOpenIndex(null)
        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (openIndex === null) return

        const onPointerDown = (event: PointerEvent): void => {
            if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenIndex(null)
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return
            buttonRefs.current[openIndex]?.focus()
            setOpenIndex(null)
        }

        document.addEventListener('pointerdown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [openIndex])

    useEffect(() => () => clearCloseTimer(), [])

    return (
        <nav ref={navRef} aria-label='Main' className='hidden h-full xl:block'>
            <ul className='flex h-full items-stretch'>
                {mainNavigation.map((item, index) => {
                    const active = isNavItemActive(item, router.asPath)

                    if (!item.links) {
                        return (
                            <li key={item.label} className='flex items-center'>
                                <Link
                                    href={item.href ?? '/'}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(topLevelClass, active && 'bg-sand')}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        )
                    }

                    const open = openIndex === index
                    const panelId = `main-nav-panel-${index}`

                    return (
                        <li
                            key={item.label}
                            className='flex items-center'
                            onPointerEnter={(event) => {
                                if (event.pointerType !== 'mouse') return
                                clearCloseTimer()
                                if (openIndex !== index) openedByHover.current = true
                                setOpenIndex(index)
                            }}
                            onPointerLeave={(event) => {
                                if (event.pointerType !== 'mouse') return
                                scheduleClose()
                            }}
                            onBlur={(event) => {
                                const next = event.relatedTarget as Node | null
                                if (!next || event.currentTarget.contains(next)) return
                                setOpenIndex((current) => (current === index ? null : current))
                            }}
                        >
                            <button
                                ref={(element) => {
                                    buttonRefs.current[index] = element
                                }}
                                type='button'
                                aria-expanded={open}
                                aria-controls={panelId}
                                onClick={() => {
                                    if (open && openedByHover.current) {
                                        openedByHover.current = false
                                        return
                                    }
                                    openedByHover.current = false
                                    setOpenIndex(open ? null : index)
                                }}
                                className={cn(topLevelClass, (open || active) && 'bg-sand')}
                            >
                                {item.label}

                                <ChevronDown
                                    aria-hidden='true'
                                    className={cn('size-4 text-gold-deep transition-transform duration-200', open && 'rotate-180')}
                                />
                            </button>

                            <div
                                id={panelId}
                                hidden={!open}
                                className='absolute inset-x-0 top-full z-50 pt-2'
                            >
                                <div className='animate-in fade-in slide-in-from-top-1 grid overflow-hidden rounded-2xl border border-border bg-white shadow-2xl shadow-navy/10 duration-200 lg:grid-cols-[18rem_1fr]'>
                                    <div className='relative bg-navy p-8 text-white'>
                                        <div className='pattern-dots absolute inset-0' aria-hidden='true' />

                                        <div className='relative'>
                                            <p className='text-xs font-semibold uppercase tracking-[0.16em] text-gold'>
                                                {item.label}
                                            </p>

                                            <p className='mt-3 font-heading text-xl leading-snug'>
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>

                                    <ul className='grid gap-1 p-4 sm:grid-cols-2 xl:grid-cols-3'>
                                        {item.links.map((link) => {
                                            const linkActive = isNavItemActive({ label: link.label, href: link.href }, router.asPath)

                                            return (
                                                <li key={`${item.label}-${link.href}`}>
                                                    <Link
                                                        href={link.href}
                                                        aria-current={linkActive ? 'page' : undefined}
                                                        className={cn(
                                                            'group flex h-full flex-col rounded-xl px-4 py-3 transition-colors hover:bg-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy',
                                                            linkActive && 'bg-sand'
                                                        )}
                                                    >
                                                        <span className='flex items-center gap-1.5 font-semibold text-navy'>
                                                            {link.label}

                                                            <ArrowRight
                                                                aria-hidden='true'
                                                                className='size-4 -translate-x-1 text-gold-deep opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100'
                                                            />
                                                        </span>

                                                        {link.description && (
                                                            <span className='mt-0.5 text-sm text-muted-foreground'>
                                                                {link.description}
                                                            </span>
                                                        )}
                                                    </Link>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </li>
                    )
                })}
            </ul>
        </nav>
    )
}
